// @LTG-ID: F-051
// @STATUS: BUILT
// @HERMETIC: true
// @DOCS: 07_PAGES_AND_ROUTES.md, 08_FEATURES.md#F-051,
//        source `Journals/sde-vs-ebitda.html` lines 263-435 (article body +
//        figure + accept/reject + pull quote + table + sources).
// @CHANGED: 2026-02 session_004 (CHG-0062)
// @OWNER: web
//
// F-051 — `/journals/sde-vs-ebitda` route. Brief 02 of The KGOB Journals.
// Reuses the F-090 `<BriefPage>` shell. Body authored inline as JSX children
// per ORCH-0050 A4. One inline-SVG figure (`<Brief02WorkedExample />`, F-094).

import BriefPage from "../components/journals/BriefPage";
import Brief02WorkedExample from "../components/journals/infographics/Brief02WorkedExample";
import ExternalCTA from "../lib/external-cta";
import {
    BRIEF_02_FRONTMATTER,
    BRIEF_02_AT_A_GLANCE,
    BRIEF_02_SOURCES,
    BRIEF_02_END_CTA,
    BRIEF_02_READ_NEXT,
    BRIEF_02_ACCEPT,
    BRIEF_02_REJECT,
} from "../data/brief-02";

export const Brief02 = () => {
    return (
        <BriefPage
            frontmatter={BRIEF_02_FRONTMATTER}
            sources={BRIEF_02_SOURCES}
            endCta={BRIEF_02_END_CTA}
            readNext={BRIEF_02_READ_NEXT}
        >
            <aside
                className="ltg-brief-glance"
                data-testid="brief02-at-a-glance"
            >
                <span className="ltg-brief-glance-label">At a glance</span>
                <dl>
                    {BRIEF_02_AT_A_GLANCE.map((row, idx) => (
                        <div key={idx} style={{ display: "contents" }}>
                            <dt>{row.dt}</dt>
                            <dd
                                dangerouslySetInnerHTML={{ __html: row.dd }}
                            />
                        </div>
                    ))}
                </dl>
            </aside>

            <p
                className="ltg-brief-lead-graf"
                data-testid="brief02-lead-graf"
            >
                Two owners with identical tax returns can walk away from a
                sale with very different proceeds. The reason almost always
                sits in the earnings line: whether buyers underwrote on
                Seller's Discretionary Earnings or on EBITDA, and which
                add-backs survived the buyer's Quality of Earnings analysis.
                This article explains the difference and shows the threshold
                where the conversation flips from one to the other.
            </p>

            <h2>Definitions, in one paragraph each</h2>
            <p>
                <strong>Seller's Discretionary Earnings</strong>, or SDE, is
                the cash flow available to a single owner-operator who works
                in the business. It is built up from net income on the tax
                return: add back interest, taxes, depreciation, and
                amortization, then add back one owner's compensation and
                benefits (because the owner is the operator), and finally
                add back legitimate one-time and discretionary expenses. SDE
                answers the question,{" "}
                <em>what does this business earn for the person running it?</em>
            </p>
            <p>
                <strong>EBITDA</strong>, or earnings before interest, taxes,
                depreciation, and amortization, is the cash flow available
                to a corporate buyer who hires a market-rate manager to run
                the business. It starts from the same place SDE does, but it
                deducts a fair-market salary for the role the owner is
                currently filling. EBITDA answers a different question,{" "}
                <em>what does this business earn for a buyer who is not also the operator?</em>
            </p>
            <p>
                The two numbers diverge by roughly one owner-salary's worth
                of cash flow, which on a $3M revenue business often means
                $150K to $250K. That gap, multiplied by whatever multiple
                applies, is real money in your settlement statement.
            </p>

            <h2>
                The inflection point sits around $1M to $2M of normalized
                earnings
            </h2>
            <p>
                Up to roughly $1M of normalized earnings, the buyer pool is
                dominated by individual buyers, SBA-financed acquirers, and
                small search funds. They are buying themselves a job, and
                they underwrite to SDE because they will be the operator.
                Above $2M of normalized earnings, the buyer pool tilts
                toward private equity, family offices, and strategic
                acquirers. They are buying a portfolio asset, and they
                underwrite to EBITDA with the operator salary baked in.
            </p>
            <p>
                Between $1M and $2M, you sit in a transition zone where some
                buyers offer on SDE and others on EBITDA. Almost every
                Quality of Earnings firm will reframe a deal in that zone
                from SDE to EBITDA during diligence; almost every individual
                buyer working through SBA financing will reframe in the
                other direction. The mismatch between which earnings number
                the seller used to set the asking price and which one the
                buyer is offering against is a leading cause of late-stage
                deal failure.
            </p>

            <Brief02WorkedExample />

            <h2>The owner's salary problem</h2>
            <p>
                Almost every owner-operator pays themselves a salary that is
                wrong for valuation purposes. Some take below-market
                compensation to keep more cash inside the business for tax
                efficiency. Some take above-market compensation because the
                business has been their primary wealth-building vehicle and
                salaries were never benchmarked. Either way, the tax return
                understates or overstates real economic earnings.
            </p>
            <p>
                A defensible normalization replaces the actual owner
                compensation with a market-rate salary for the role the
                owner is filling. For a $3M revenue HVAC company, the
                operator role typically benchmarks at $150K to $200K plus
                10% to 20% in benefits. If the owner pays themselves $80K,
                the normalization adds back the gap; if the owner pays
                themselves $400K, the normalization subtracts the gap. The
                point is not the direction; the point is that EBITDA
                without a normalized operator salary is not EBITDA, and
                buyers will adjust it during diligence regardless.
            </p>

            <h2>Add-backs buyers accept, and add-backs buyers reject</h2>
            <p>
                The most contested territory in any sale-prep conversation
                is the add-back list. Sellers and their brokers want to
                maximize normalized earnings; buyers and their Quality of
                Earnings advisors want to minimize them. The settled middle
                ground is reasonably well-defined and worth knowing in
                advance.
            </p>

            <div
                className="ltg-brief-accept-reject"
                data-testid="brief02-accept-reject"
            >
                <div className="ltg-brief-ar-card ltg-brief-ar-card--accept">
                    <h4>
                        <span className="ltg-brief-ar-card__mark">+</span>{" "}
                        Buyers typically accept
                    </h4>
                    <ul>
                        {BRIEF_02_ACCEPT.map((item, idx) => (
                            <li key={idx}>{item}</li>
                        ))}
                    </ul>
                </div>
                <div className="ltg-brief-ar-card ltg-brief-ar-card--reject">
                    <h4>
                        <span className="ltg-brief-ar-card__mark">−</span>{" "}
                        Buyers typically reject
                    </h4>
                    <ul>
                        {BRIEF_02_REJECT.map((item, idx) => (
                            <li key={idx}>{item}</li>
                        ))}
                    </ul>
                </div>
            </div>

            <h2>How a QofE firm tests every add-back</h2>
            <p>
                A Quality of Earnings analysis, or QofE, is the buyer-side
                audit of your normalized-earnings story. A national QofE
                firm typically charges $25,000 to $100,000 for a
                private-business analysis and produces a report that walks
                line-by-line through every add-back on the seller's offering
                memorandum. For each, the firm asks four questions: Is it
                documented in the accounting system? Is it consistently
                characterized over the trailing twelve months? Will the
                cost recur for the next owner? Is the substitute cost (a
                manager salary, an arms-length rent, a market-rate insurance
                premium) at fair market value?
            </p>

            <blockquote
                className="ltg-brief-pull-quote"
                data-testid="brief02-pull-quote"
            >
                <p>
                    "Owners are almost always surprised by how much of their
                    add-back list gets reduced in QofE. The fix is not to
                    add fewer add-backs; it is to document every one of them
                    properly, at the time you incur them, on the way to the
                    sale."
                </p>
                <cite>
                    Sara F. Gonzalez, CPA &middot; Managing Partner, KGOB
                </cite>
            </blockquote>

            <p>
                The owners who do best in QofE prepared their books two
                years in advance, ran a "sell-side" QofE on themselves six
                months before going to market, and arrived at the buyer's
                QofE with a documented answer to every line. The owners who
                do worst arrived with a clean tax return, a hopeful add-back
                list, and no documentation. The math is unforgiving in the
                second case.
            </p>

            <div
                className="ltg-brief-inline-cta"
                data-testid="brief02-inline-cta"
            >
                <p className="ltg-brief-inline-cta-text">
                    The Strategic Snapshot includes a defensible{" "}
                    <em>normalized-earnings</em> calculation built to QofE
                    standards.
                </p>
                <ExternalCTA
                    ctaId="brief-02-order"
                    className="ltg-brief-inline-cta-btn"
                    data-testid="brief02-inline-cta-btn"
                >
                    Get Your Valuation →
                </ExternalCTA>
            </div>

            <h2>
                What "Adjusted EBITDA" means in an LOI versus at closing
            </h2>
            <p>
                Letters of intent almost always quote a multiple on
                "Adjusted EBITDA" without defining the adjustments. That
                ambiguity is intentional on the buyer's side. By the time
                the working-capital target and the final QofE are settled,
                the Adjusted EBITDA number used at closing is frequently 5%
                to 15% lower than the number that was on the LOI. The
                multiple is the same; the EBITDA shrank.
            </p>
            <p>
                Two pieces of preparation defend against this. First, the
                seller should run their own pre-LOI normalized-earnings
                analysis, documented and footnoted, and use that number as
                the basis for any LOI conversation. Second, the seller's
                representation in the definitive agreement should reference
                an exhibit with each add-back enumerated, so that any
                disputed item at closing is a defined object, not a moving
                target.
            </p>

            <h2>
                Why your tax return understates your value, and how to stop
                apologizing for it
            </h2>
            <p>
                Almost every owner-operated business presents worse on the
                tax return than it actually performs. The reason is
                structural: every dollar of taxable income is a dollar of
                cash tax for the owner, so the rational tax-management
                posture is to deploy expenses against income wherever the
                tax code permits. That is not aggressive; that is what tax
                professionals are paid to do.
            </p>
            <p>
                The translation problem at sale is that the buyer is looking
                at the tax return and the seller is talking about the real
                economic earnings. The bridge between the two is the
                normalization. A defensible normalization, signed by a CPA,
                gets you to the number the business actually earns; a
                defensible Quality of Earnings exercise gets a buyer
                comfortable with that number. The work is not embarrassing;
                it is the standard practice in every well-run sale process.
            </p>

            <h2>
                Where your business lands on the SDE-to-EBITDA continuum,
                and what to do about it
            </h2>
            <p>
                Three quick tests put your business on the continuum.
                First, what is your trailing-twelve-month net income plus
                owner comp plus interest, taxes, depreciation, and
                amortization? That is your SDE. If it is under $1M, almost
                every realistic buyer is in the SDE world. Second, replace
                your compensation with a market-rate manager. The result is
                your EBITDA. If it is above $2M, you are firmly in the
                EBITDA world. Third, if you are between $1M and $2M, you
                are in the transition zone, and the work of preparation is
                largely about which side you want to land on at sale.
            </p>
            <p>
                Brief 1 in this library covers the broader valuation
                primer. Brief 3 covers the three approaches that produce
                defensible multiples. Brief 29 covers the working-capital
                and escrow mechanics that determine what reaches your
                account after closing. Together with this brief, those four
                pieces form the core literacy every owner needs before the
                first conversation with a banker.
            </p>

            <div
                className="ltg-brief-takeaway"
                data-testid="brief02-takeaway"
            >
                <span className="ltg-brief-takeaway-label">Takeaway</span>
                <p>
                    Below roughly $2M of normalized earnings, SDE is your
                    number. Above it, EBITDA is. The transition is not
                    cosmetic; it changes who your buyer pool is, who
                    finances the deal, and what your add-backs are allowed
                    to look like.
                </p>
            </div>
        </BriefPage>
    );
};

export default Brief02;
